import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

export const PDF_FORMAT_DEFAULT_KEY = "pdfFormatDefault";

/** When `'true'` in localStorage, PDF downloads omit display-clue wording (dictionary-style clues; REST `useDisplayClue=false`). */
export const PDF_DOWNLOAD_DICTIONARY_KEY = "pdfDownloadDictionary";

/** @returns whether the next PDF download should pass `useDisplayClue=true` to the API (default true). */
export function getPdfDownloadUseDisplayClue() {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(PDF_DOWNLOAD_DICTIONARY_KEY) !== "true";
}

const FORMAT_OPTIONS = [
    { value: "letter", label: "Letter", size: "8.5\" × 11\" (216 × 279 mm)" },
    { value: "a4", label: "A4", size: "210 × 297 mm (8.27\" × 11.69\")" }
];

/**
 * Dialog to select PDF page format (Letter or A4) before downloading.
 * Used by cross board published step, crossword play, and grid/word search play.
 * @param {string} [defaultFormat] - Default format from user settings (e.g. from account/settings)
 * @param {function(string)} [onSetDefaultFormat] - Called when user checks "Set as default" and downloads
 */
export default function PdfFormatDialog({ open, onClose, onConfirm, defaultFormat, onSetDefaultFormat }) {
    const [format, setFormat] = useState("letter");
    const [setAsDefault, setSetAsDefault] = useState(false);

    useEffect(() => {
        if (open) {
            const saved = defaultFormat ?? (typeof window !== "undefined" ? localStorage.getItem(PDF_FORMAT_DEFAULT_KEY) : null) ?? "letter";
            setFormat(saved === "a4" ? "a4" : "letter");
            setSetAsDefault(false);
        }
    }, [open, defaultFormat]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="pdf-format-dialog-title">
            <DialogTitle id="pdf-format-dialog-title" sx={{ position: "relative", pr: 5 }}>
                Download PDF
                <Tooltip title="Close">
                    <IconButton
                        aria-label="Close"
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)"
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select page format for the PDF:
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexDirection: "row", justifyContent: "center" }}>
                    {FORMAT_OPTIONS.map((opt) => (
                        <Box
                            key={opt.value}
                            onClick={() => setFormat(opt.value)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setFormat(opt.value);
                                }
                            }}
                            sx={{
                                width: 100,
                                aspectRatio: "1/1.3",
                                p: 2,
                                borderRadius: 2,
                                border: 2,
                                borderColor: format === opt.value ? "success.main" : "divider",
                                bgcolor: format === opt.value ? "success.main" : "background.paper",
                                color: format === opt.value ? "#fff" : "text.primary",
                                cursor: "pointer",
                                "& .MuiTypography-root": { color: "inherit" },
                                textAlign: "center",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                transition: "border-color 0.2s, background-color 0.2s",
                                outline: "none",
                                "&:hover": {
                                    borderColor: "success.main",
                                    bgcolor: format === opt.value ? "success.light" : "success.light",
                                    "& .MuiTypography-root": { color: "inherit" }
                                },
                                "&:focus-visible": {
                                    borderColor: "success.main",
                                    boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.3)"
                                }
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                {opt.label}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                                {opt.size}
                            </Typography>
                            <Tooltip title="Download">
                                <IconButton
                                    color="inherit"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (setAsDefault && typeof window !== "undefined") {
                                            localStorage.setItem(PDF_FORMAT_DEFAULT_KEY, opt.value);
                                            onSetDefaultFormat?.(opt.value);
                                        }
                                        onConfirm?.(opt.value);
                                        onClose?.();
                                    }}
                                    sx={{ mt: "auto", pt: 1 }}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={setAsDefault}
                            onChange={(e) => setSetAsDefault(e.target.checked)}
                            color="primary"
                            size="small"
                        />
                    }
                    label={
                        <Typography variant="body2" color="text.secondary">
                            Set this as default format
                        </Typography>
                    }
                    sx={{ mt: 2 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
